// API Response types

export interface Organization {
  id: string;
  name: string;
  slug: string;
  isActive: boolean;
  /** Who sold them their fleet. Null for one that predates the partner chain. */
  partnerId?: string | null;
  partner?: { id: string; name: string; slug: string } | null;
  createdAt: string;
  updatedAt: string;
  stats?: {
    users: number;
    vehicles: number;
    drivers: number;
    geofences: number;
    groups?: number;
    geofenceGroups?: number;
  };
}

export const USER_ROLES = ['USER', 'MANAGER', 'ADMIN', 'SUPER_ADMIN'] as const;
export type UserRole = (typeof USER_ROLES)[number];

export interface User {
  id: string;
  externalAuthId: string;
  email: string | null;
  name: string | null;
  /**
   * `MANAGER` was missing here while the backend already treated it as a
   * first-class role — `UNRESTRICTED_ROLES` grants a manager every vehicle in
   * the organisation — so a real manager rendered as an unrecognised role.
   */
  role: UserRole;
  isActive?: boolean;
  createdAt: string;
  updatedAt: string;
  /** Newest session start, or null for an account nobody has ever signed into. */
  lastSignInAt?: string | null;
  /** Sessions that have not expired. */
  activeSessions?: number;
  organization: {
    id: string;
    name: string;
    slug: string;
  };
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: Pagination;
}

export type OrganizationsResponse = PaginatedResponse<Organization>;
export type UsersResponse = PaginatedResponse<User>;

// ============================================
// Audit trail
// ============================================

export interface AuditEntry {
  id: string;
  organizationId: string | null;
  action: string;
  actorId: string | null;
  actorEmail: string | null;
  actorIp: string | null;
  targetType: string | null;
  targetId: string | null;
  targetName: string | null;
  changes: { before?: Record<string, unknown>; after?: Record<string, unknown> } | null;
  result: 'success' | 'failure';
  reason: string | null;
  createdAt: string;
}

export type AuditResponse = PaginatedResponse<AuditEntry>;

// ============================================
// Fleet health
// ============================================

export interface DeviceActivity {
  ident: string;
  messages: number;
  lastSeen: string;
  maxSpeed: number;
}

export interface FleetHealth {
  ingest: {
    state: 'ok' | 'stale' | 'unknown';
    lastMessageAt: string | null;
    secondsSinceLastMessage: number | null;
    thresholdSeconds: number;
  } | null;
  unclaimed: DeviceActivity[];
  silent: Array<{ id: string; name: string; ident: string | null }>;
  freshness: Array<{
    id: string;
    name: string;
    ident: string;
    lastSeen: string;
    secondsSince: number;
    messages: number;
  }>;
  counts: { unclaimed: number; silent: number; reporting: number };
  window: { days: number; since: string };
}

// ============================================
// Retention
// ============================================

export interface TableUsage {
  table: string;
  rows: number;
  oldest: string | null;
  /** Null means kept for ever. */
  days: number | null;
  reason: string;
  expired: number;
}

export interface Retention {
  telemetry: { store: string; days: number; reason: string };
  tables: TableUsage[];
  pruningEnabled: boolean;
  expiredTotal: number;
}

// ============================================
// Sensor mappings
// ============================================

export type SensorDefinition =
  | {
      type: 'measurement';
      sources: string[];
      unit?: string;
      scale: number;
      offset: number;
      calibration?: { raw: number; value: number }[];
    }
  | { type: 'digital'; sources: string[]; bit?: number; invert: boolean }
  | { type: 'state'; sources: string[]; states: Record<string, string> }
  | { type: 'virtual'; expr: string };

export type SensorDefinitions = Record<string, SensorDefinition>;

export interface StoredMapping {
  trackerType: string;
  organizationId: string | null;
  definitions: SensorDefinitions;
  updatedAt: string;
}

export interface TrackerMapping {
  trackerType: string;
  /** What every fleet inherits. Absent until a super admin writes one. */
  system?: StoredMapping;
  /** This fleet's overrides, merged over the system rung by sensor name. */
  organization?: StoredMapping;
}

export interface SensorPreviewRow {
  name: string;
  value: number | string | boolean | null;
}

// ============================================
// Calculators
// ============================================

export type Selector =
  | { type: 'expression'; expr: string }
  | { type: 'geofence'; geofences: string[]; inside: boolean }
  | { type: 'datetime'; days: number[]; from?: string; to?: string }
  | { type: 'activity'; calculator: string };

export type Counter =
  | { type: 'parameter'; sensor: string; method: string; as: string }
  | { type: 'duration'; as: string }
  | { type: 'route'; as: string }
  | { type: 'distance'; as: string };

export interface Behavior {
  minActiveS: number;
  maxActiveS: number | null;
  maxInactiveS: number;
  minDurationS: number;
  maxDurationS: number | null;
  minDistanceKm: number | null;
}

export interface Calculator {
  slug: string;
  name: string;
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  isEnabled: boolean;
  partitionBy: { type: 'geofence' } | { type: 'sensor'; sensor: string } | null;
  selectors: Selector[];
  behavior: Behavior;
  counters: Counter[];
  notifications: { onStart: { delayS?: number } | null; onEnd: object | null };
}

export type CalculatorsProfile = Record<string, Calculator>;

export interface ProfileList {
  kind: string;
  /** What the shipped bundle says, before any override. */
  defaults: CalculatorsProfile;
  /** Defaults with this organisation's overrides applied — what the engine runs. */
  effective: CalculatorsProfile;
  profiles: { id: string; scope: string; version: number; config: CalculatorsProfile }[];
}

// ============================================
// Interval backfill
// ============================================

export interface BackfillResult {
  messagesReplayed: number;
  intervalsWritten: number;
  /** Rows removed before the replay, so the range is not counted twice. */
  intervalsReplaced: number;
}

// ============================================
// Partners: the distribution chain
// ============================================

export interface PartnerCredentialSummary {
  /** `••••abcd`. The token itself is never served. */
  hint: string;
  provider?: string;
  lastCheckOk?: boolean | null;
  lastCheckedAt?: string | null;
  lastCheckError?: string | null;
}

export interface Partner {
  id: string;
  name: string;
  slug: string;
  /** Null for an integrator; the integrator's id for a sub-dealer. */
  parentId: string | null;
  contactEmail: string | null;
  notes: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  credential?: PartnerCredentialSummary | null;
  stats?: {
    children: number;
    channels: number;
    devices: number;
    allocated: number;
    organizations: number;
  };
}

export interface PartnerOrganization {
  id: string;
  name: string;
  slug: string;
  isActive: boolean;
  /** Which partner in the subtree this customer belongs to. */
  partner: { id: string; name: string } | null;
  /** False for a customer of a sub-dealer below the partner being viewed. */
  isOwn: boolean;
  stats: { vehicles: number; users: number };
}

/** The detail endpoint answers with pool counts where the list answers with totals. */
export interface PartnerDetail extends Omit<Partner, 'stats'> {
  children: Partner[];
  organizations: PartnerOrganization[];
  /**
   * Which partner in the chain holds the token, and therefore the pool. This
   * partner itself when it has its own; an ancestor when it sells on one;
   * null when nobody in the chain has stored a token at all.
   */
  tokenHolder: { id: string; name: string } | null;
  stats: { devices: number; free: number; allocated: number };
}

export interface PartnerChannel {
  id: string;
  providerChannelId: string;
  name: string;
  protocolName: string | null;
  isActive: boolean;
  syncedAt: string;
  devices: number;
}

/** A device in a partner's pool. Allocated when `vehicleId` is set. */
export interface PoolDevice {
  id: string;
  providerDeviceId: string;
  ident: string | null;
  name: string;
  deviceTypeName: string | null;
  phone: string | null;
  syncedAt: string;
  allocatedAt: string | null;
  organizationId: string | null;
  vehicleId: string | null;
  /** When it last reported, from ClickHouse. Null when it never has. */
  lastSeenAt: string | null;
  channel: { id: string; name: string; protocolName: string | null; isActive: boolean } | null;
  organization: { id: string; name: string; slug: string } | null;
  vehicle: { id: string; name: string; plateNumber: string; isActive: boolean } | null;
}

export interface SyncResult {
  channels: number;
  devices: number;
  removed: number;
  tokenOwnerId: string;
  isInherited: boolean;
}

export interface CredentialTestResult {
  ok: boolean;
  error: string | null;
  tokenOwnerId: string;
  isInherited: boolean;
}

// ============================================
// Units, across organisations
// ============================================

export const ASSET_CLASSES = ['POWERED', 'UNPOWERED'] as const;
export type AssetClass = (typeof ASSET_CLASSES)[number];

export interface AdminVehicle {
  id: string;
  name: string;
  plateNumber: string;
  ident: string | null;
  providerDeviceId: string | null;
  provider: string | null;
  brand: string | null;
  model: string | null;
  year: number | null;
  color: string | null;
  vin: string | null;
  assetClass: AssetClass;
  fuelTankVolume: number | null;
  initialMileage: number | null;
  speedLimit: number;
  sensorConfig: Record<string, unknown>;
  isActive: boolean;
  /** Set by an allocation; the floor for reads of raw history. */
  allocatedAt: string | null;
  organizationId: string;
  groupId: string | null;
  driverId: string | null;
  createdAt: string;
  updatedAt: string;
  PartnerDevice: { id: string; partnerId: string; providerDeviceId: string } | null;
}

export interface OrganizationFleet {
  organization: {
    id: string;
    name: string;
    slug: string;
    isActive: boolean;
    partnerId: string | null;
  };
  data: AdminVehicle[];
  groups: { id: string; name: string }[];
  drivers: { id: string; name: string }[];
}
